/**
 * Browser-side bug-report helpers shared by every web client that files into
 * the house `turbotwin-bug-report` module (platform webclient, customer
 * portals, studio). Import as `turbotwin-sdk/bug-report`; browser only, no
 * Node polyfills, so it never goes through the package root.
 *
 * Five pieces, all optional and independent:
 *
 *  1. Session log: `installSessionLog()` wraps the console methods (plus
 *     window `error` / `unhandledrejection`, and route changes) into a bounded
 *     ring buffer. Call it before anything else runs so boot logs land too.
 *     `getSessionLogText()` renders it, with a system-info header, into the
 *     `session.log` attachment body.
 *  2. `buildSystemInfoText()`: one-line-per-fact system summary for the
 *     `system_info` reducer parameter. Everything is read at call time.
 *  3. `captureTabScreenshot()`: one-frame getDisplayMedia grab of the current
 *     tab (the interesting pixels are usually a WebGL canvas in an iframe,
 *     which DOM rasterizers render blank). Must run inside the user activation
 *     of a click, with the caller's own dialog hidden.
 *  4. Attachment recipe: bytes never go through a reducer. Presign
 *     (`request_attachment_upload_url` / `request_image_upload_url`), HTTP PUT
 *     straight to blob storage, then `register_attachment` /
 *     `register_image_attachment` on the same connection that submitted the
 *     report (both are reporter-only server-side).
 *  5. App context + redactors: `setReportContext()` adds the app's own
 *     `Name: value` lines to system info (and so to the session.log header);
 *     `setReportRedactor()` rewrites the text before it leaves. System info
 *     and session.log are redacted here; the app passes summary and
 *     description through `redactReportText()` itself.
 */

// ---------------------------------------------------------------------------
// session log
// ---------------------------------------------------------------------------

const MAX_ENTRIES = 4000;
const MAX_TOTAL_CHARS = 1_000_000; // ~1 MB of text; the module's log cap is 8 MB
const MAX_ARG_CHARS = 2000;

interface LogEntry {
  at: number;
  level: string;
  text: string;
}

const entries: LogEntry[] = [];
let totalChars = 0;
let dropped = 0;
let appName = 'TurboTwin';
let appBuild: string | undefined;

function pushEntry(level: string, text: string) {
  entries.push({ at: Date.now(), level, text });
  totalChars += text.length;
  while (entries.length > MAX_ENTRIES || (totalChars > MAX_TOTAL_CHARS && entries.length > 1)) {
    const first = entries.shift();
    if (!first) break;
    totalChars -= first.text.length;
    dropped++;
  }
}

function clipArg(s: string): string {
  return s.length > MAX_ARG_CHARS ? `${s.slice(0, MAX_ARG_CHARS)}…[truncated]` : s;
}

function fmtArg(arg: unknown): string {
  try {
    if (typeof arg === 'string') return arg;
    if (arg instanceof Error) {
      return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ''}`;
    }
    const s = JSON.stringify(arg);
    return s === undefined ? String(arg) : s;
  } catch {
    // Circular structures, revoked proxies, hostile toString: give up gently.
    try {
      return Object.prototype.toString.call(arg);
    } catch {
      return '<unserializable>';
    }
  }
}

/**
 * Render console arguments the way devtools would: `%s %d %i %f %o %O %j` in
 * a leading format string consume the next argument, `%c` consumes its CSS
 * argument and prints nothing, `%%` is a literal percent. Leftover arguments
 * follow, space-separated. Without this the SpacetimeDB SDK's styled logger
 * lands in session.log as `%cINFO%c Connecting... color: #fff; ...`.
 */
export function formatConsoleArgs(args: unknown[]): string {
  const rest = [...args];
  let text = '';
  if (typeof rest[0] === 'string' && /%[sdifoOjc%]/.test(rest[0])) {
    const fmt = rest.shift() as string;
    text = clipArg(fmt).replace(/%([sdifoOjc%])/g, (m, spec: string) => {
      if (spec === '%') return '%';
      if (rest.length === 0) return m;
      const arg = rest.shift();
      if (spec === 'c') return '';
      if (spec === 'd' || spec === 'i') return String(typeof arg === 'number' ? Math.trunc(arg) : Number(arg));
      if (spec === 'f') return String(Number(arg));
      return clipArg(fmtArg(arg));
    });
  }
  return [text, ...rest.map((a) => clipArg(fmtArg(a)))].filter((s) => s !== '').join(' ');
}

function record(level: string, args: unknown[]) {
  try {
    const text = formatConsoleArgs(args).trim();
    if (text) pushEntry(level, text);
  } catch {
    /* never break the console */
  }
}

const INSTALL_FLAG = '__ttSessionLogInstalled';

/**
 * Per-module socket state, reported by the host's connection layer and
 * summarised in system info. `released` is a deliberate pool release (org or
 * project switch) and ages out of the summary; `disconnected` and `error`
 * never do, since those are the ones a report is about.
 */
const connections = new Map<string, { state: string; at: number }>();
const RELEASED_TTL_MS = 10 * 60_000;
export function noteConnectionState(moduleName: string, state: string): void {
  connections.set(moduleName, { state, at: Date.now() });
}
function connectionSummary(now: number): string {
  return [...connections]
    .filter(([, c]) => c.state !== 'released' || now - c.at < RELEASED_TTL_MS)
    .map(([name, c]) => `${name}=${c.state} ${Math.round((now - c.at) / 1000)}s ago`)
    .join(', ');
}

// ---------------------------------------------------------------------------
// app context and redaction
// ---------------------------------------------------------------------------

/**
 * Built-in system-info keys, reserved. The module's issue formatter copies the
 * FIRST `Build:` / `URL:` line to a linked GitHub issue, so an app line under a
 * built-in name would leak there whenever the built-in line is absent (no
 * `build` option, or no window).
 */
const BUILTIN_KEYS = [
  'Time',
  'App',
  'Build',
  'Connections',
  'URL',
  'Host',
  'UserAgent',
  'Language',
  'Online',
  'Screen',
  'Viewport',
  'Timezone',
  'CPU',
  'DeviceMemory',
  'GPU',
  'JSHeap',
];

/** Returns the value for this report, or undefined / '' to add no line. */
export type ReportContextProvider = () => string | undefined;
export type ReportRedactor = (text: string) => string;

const contextProviders = new Map<string, ReportContextProvider>();
const redactors = new Map<string, ReportRedactor>();

/**
 * Add a `name: value` line to system info, read each time a report is built.
 * The same name again replaces it (HMR safe); `null` removes it. Built-in names
 * are reserved, and a name holds no colon or newline (one fact per line).
 */
export function setReportContext(name: string, provider: ReportContextProvider | null): void {
  if (!name || /[:\r\n]/.test(name) || BUILTIN_KEYS.includes(name)) {
    throw new Error(`setReportContext: invalid or reserved name "${name}"`);
  }
  if (provider) contextProviders.set(name, provider);
  else contextProviders.delete(name);
}

/** Add a redactor for every text part of a report. The same name again replaces it; `null` removes it. */
export function setReportRedactor(name: string, redactor: ReportRedactor | null): void {
  if (redactor) redactors.set(name, redactor);
  else redactors.delete(name);
}

/**
 * Run every redactor in registration order. Fails closed: a redactor that
 * throws or returns a non-string turns the whole part into a marker, so
 * unredacted text never ships. Call it on summary and description; system info
 * and the session log are already redacted.
 */
export function redactReportText(text: string): string {
  let out = text;
  for (const [name, redact] of redactors) {
    try {
      const next: unknown = redact(out);
      if (typeof next !== 'string') return `[redaction failed: ${name}]`;
      out = next;
    } catch {
      return `[redaction failed: ${name}]`;
    }
  }
  return out;
}

function contextLines(): string[] {
  const lines: string[] = [];
  for (const [name, provider] of contextProviders) {
    let value: string;
    try {
      const v = provider();
      if (v === undefined || v === '') continue;
      value = String(v);
    } catch (err) {
      // Formatting a thrown value can throw too (a null-prototype object, a Symbol message).
      let msg = 'unknown error';
      try {
        msg = err instanceof Error ? String(err.message) : String(err);
      } catch {
        /* keep the fallback */
      }
      value = `<context provider failed: ${msg}>`;
    }
    // One fact per line: the module's line parser relies on it.
    lines.push(`${name}: ${clipArg(value.replace(/[\r\n]+/g, ' '))}`);
  }
  return lines;
}

/** WebGPU adapter summary, probed once at install (async, so cached for the sync header). */
let gpuInfo = '';

function recordRoute() {
  record('nav', [`${location.pathname}${location.search}${location.hash}`]);
}

export interface SessionLogOptions {
  /** Shown in the session.log header, e.g. "TurboTwin webclient". */
  appName?: string;
  /** Build identifier for the `Build:` system-info line (a version or commit). */
  build?: string;
}

/** Idempotent (HMR / double-import safe). Call once, as early as possible. */
export function installSessionLog(options: SessionLogOptions = {}): void {
  if (options.appName) appName = options.appName;
  if (options.build) appBuild = options.build;
  const g = globalThis as Record<string, unknown>;
  if (g[INSTALL_FLAG]) return;
  g[INSTALL_FLAG] = true;

  const levels = ['log', 'info', 'warn', 'error', 'debug'] as const;
  for (const level of levels) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      record(level, args);
      original(...args);
    };
  }

  if (typeof window === 'undefined') {
    record('info', ['[sessionLog] capture installed (no window)']);
    return;
  }

  window.addEventListener('error', (ev) => {
    record('window.error', [
      ev.message,
      ev.filename ? `${ev.filename}:${ev.lineno}:${ev.colno}` : '',
      ev.error instanceof Error ? ev.error : '',
    ]);
  });
  window.addEventListener('unhandledrejection', (ev) => {
    record('unhandledrejection', [ev.reason instanceof Error ? ev.reason : fmtArg(ev.reason)]);
  });

  // Route history: pushState/replaceState are how react-router navigates,
  // popstate is the Back button. Each lands as a NAV line in session.log.
  for (const m of ['pushState', 'replaceState'] as const) {
    const original = history[m].bind(history);
    history[m] = (...args: Parameters<History['pushState']>) => {
      original(...args);
      recordRoute();
    };
  }
  window.addEventListener('popstate', recordRoute);

  try {
    (navigator as { gpu?: { requestAdapter(): Promise<unknown> } }).gpu
      ?.requestAdapter()
      .then((a) => {
        const ad = a as {
          info?: { vendor?: string; architecture?: string; device?: string; description?: string };
          limits?: { maxBufferSize?: number; maxTextureDimension2D?: number };
        } | null;
        if (!ad) return;
        const i = ad.info ?? {};
        gpuInfo = [i.vendor, i.architecture, i.device, i.description].filter(Boolean).join(' / ');
        if (ad.limits?.maxBufferSize) {
          gpuInfo += ` (maxBuffer ${(ad.limits.maxBufferSize / 1048576).toFixed(0)} MB, maxTex2D ${ad.limits.maxTextureDimension2D})`;
        }
      })
      .catch(() => {});
  } catch {
    /* no WebGPU */
  }

  record('info', ['[sessionLog] capture installed']);
}

/**
 * One-line-per-fact system summary, used both as the `system_info` reducer
 * param and as the header of the session.log attachment. Mirrors the Unreal
 * client's GetSystemInformation blob. Safe to call without a window (returns
 * the time, build and app context lines only). App context lines follow the
 * built-in ones, and the whole text is redacted.
 */
export function buildSystemInfoText(): string {
  return redactReportText(systemInfoRaw());
}

function systemInfoRaw(): string {
  const lines: string[] = [];
  const push = (k: string, v: unknown) => {
    try {
      if (v !== undefined && v !== null && v !== '') lines.push(`${k}: ${String(v)}`);
    } catch {
      /* skip */
    }
  };
  push('Time', new Date().toISOString());
  push('App', appName);
  push('Build', appBuild);
  push('Connections', connectionSummary(Date.now()));
  if (typeof window === 'undefined') return [...lines, ...contextLines()].join('\n');
  push('URL', window.location.href);
  push('Host', window.location.hostname);
  push('UserAgent', navigator.userAgent);
  push('Language', navigator.language);
  push('Online', navigator.onLine);
  push('Screen', `${window.screen?.width}x${window.screen?.height} @${window.devicePixelRatio}x`);
  push('Viewport', `${window.innerWidth}x${window.innerHeight}`);
  try {
    push('Timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    /* skip */
  }
  push('CPU', navigator.hardwareConcurrency);
  push('DeviceMemory', (navigator as { deviceMemory?: number }).deviceMemory);
  push('GPU', gpuInfo);
  const mem = (performance as { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
  if (mem) {
    push(
      'JSHeap',
      `${(mem.usedJSHeapSize / 1048576).toFixed(1)} MB used / ${(mem.jsHeapSizeLimit / 1048576).toFixed(0)} MB limit`,
    );
  }
  return [...lines, ...contextLines()].join('\n');
}

/** Render the captured console output into the session.log attachment body, redacted. */
export function getSessionLogText(): string {
  const header = [
    `=== ${appName} session log ===`,
    systemInfoRaw(),
    dropped > 0 ? `(${dropped} earlier entries dropped by the ring buffer)` : '',
    '',
  ]
    .filter((l) => l !== '')
    .join('\n');
  const body = entries.map((e) => `[${new Date(e.at).toISOString()}] ${e.level.toUpperCase().padEnd(5)} ${e.text}`).join('\n');
  return redactReportText(`${header}\n${body}\n`);
}

// ---------------------------------------------------------------------------
// tab screenshot
// ---------------------------------------------------------------------------

// Chromium screen-capture hints not yet in the TS DOM lib.
type DisplayMediaOptions = {
  video: { displaySurface?: string };
  audio: boolean;
  preferCurrentTab?: boolean;
  selfBrowserSurface?: 'include' | 'exclude';
  surfaceSwitching?: 'include' | 'exclude';
  monitorTypeSurfaces?: 'include' | 'exclude';
};

/** Client-side ceiling under the module's MAX_SCREENSHOT_BYTES (15 MB). */
const MAX_PNG_BYTES = 14 * 1024 * 1024;

function encodePng(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

/**
 * One-frame PNG of the current tab, or null when capture is unsupported or the
 * user declines (the caller then files the report without it). Chromium
 * pre-selects "this tab" via preferCurrentTab; other browsers show their share
 * picker. The caller hides its own UI first and calls this within the
 * transient user activation of the Submit click.
 */
export async function captureTabScreenshot(): Promise<Blob | null> {
  const md = navigator.mediaDevices as (MediaDevices & { getDisplayMedia?: (o: unknown) => Promise<MediaStream> }) | undefined;
  if (!md?.getDisplayMedia) return null;

  let stream: MediaStream | null = null;
  try {
    const options: DisplayMediaOptions = {
      video: { displaySurface: 'browser' },
      audio: false,
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
      monitorTypeSurfaces: 'exclude',
    };
    stream = await md.getDisplayMedia(options);

    const video = document.createElement('video');
    video.muted = true;
    video.srcObject = stream;
    await video.play();

    // play() can resolve before the first real frame arrives; wait for one.
    await new Promise<void>((resolve) => {
      const v = video as HTMLVideoElement & {
        requestVideoFrameCallback?: (cb: () => void) => void;
      };
      if (v.requestVideoFrameCallback) v.requestVideoFrameCallback(() => resolve());
      else setTimeout(resolve, 300);
    });

    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return null;

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx2d = canvas.getContext('2d');
    if (!ctx2d) return null;
    ctx2d.drawImage(video, 0, 0, w, h);

    let blob = await encodePng(canvas);
    if (blob && blob.size > MAX_PNG_BYTES) {
      // Rare (4K+ HDR tabs): downscale once rather than fail the upload.
      const scaled = document.createElement('canvas');
      scaled.width = Math.round(w * 0.6);
      scaled.height = Math.round(h * 0.6);
      const sctx = scaled.getContext('2d');
      if (sctx) {
        sctx.drawImage(canvas, 0, 0, scaled.width, scaled.height);
        blob = await encodePng(scaled);
      }
    }
    return blob && blob.size <= MAX_PNG_BYTES ? blob : null;
  } catch {
    return null; // user declined, or capture unavailable in this context
  } finally {
    stream?.getTracks().forEach((t) => t.stop());
  }
}

// ---------------------------------------------------------------------------
// user-picked images: client-side mirror of the module's rules
// ---------------------------------------------------------------------------

/** Server-side cap on user-attached images per report (MAX_USER_IMAGES). */
export const MAX_REPORT_IMAGES = 5;
/** Server rejects size 0 and anything over 15 MB. */
export const MAX_IMAGE_BYTES = 15 * 1024 * 1024;

/**
 * Extension allowlist and PUT Content-Type map: the same five extensions the
 * module validates (lowercase-compared). Keyed by extension, not by MIME, so a
 * mislabelled `file.type` can't sneak an unsupported format past us.
 */
export const IMAGE_CONTENT_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
};

/** Lowercased extension from a file NAME (`Shot.PNG` -> `png`), or null when there is none. */
export function imageExtension(name: string): string | null {
  const i = name.lastIndexOf('.');
  if (i <= 0 || i === name.length - 1) return null;
  return name.slice(i + 1).toLowerCase();
}

/** Content-Type for the blob PUT; `file.type` is only a fallback. */
export function imageContentType(ext: string, fallback?: string): string {
  return IMAGE_CONTENT_TYPES[ext] ?? (fallback || 'application/octet-stream');
}

/** Error text for a file the server would refuse, or null when it is attachable. */
export function validateImageFile(file: { name: string; size: number }): string | null {
  const ext = imageExtension(file.name);
  if (!ext || !(ext in IMAGE_CONTENT_TYPES)) {
    return `${file.name}: not a supported image (png, jpg, jpeg, webp, gif).`;
  }
  if (file.size === 0) {
    return `${file.name}: file is empty.`;
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return `${file.name}: larger than 15MB.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// attachment upload: presign -> PUT -> register
// ---------------------------------------------------------------------------

export type ReportAttachmentKind = 'Screenshot' | 'Log';

export interface UploadUrlTarget {
  url: string;
  blobPath: string;
  filename: string;
}

/**
 * Parse the presign reply: a JSON string envelope
 * `{"success":true,"data":{"url","blob_path","filename","expires_in"}}` or
 * `{"success":false,"error":…}`. Procedures sometimes double-encode (the first
 * JSON.parse yields a string; parse again). Throws the server's `error` text on
 * a failure envelope, and a generic error for anything unrecognisable,
 * including a non-http(s) `url`, which would otherwise resolve relative to the
 * page and 200 with index.html.
 */
export function parseUploadUrlEnvelope(raw: unknown): UploadUrlTarget {
  let parsed: unknown = raw;
  try {
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    if (typeof parsed === 'string') parsed = JSON.parse(parsed); // double-encoded
  } catch {
    throw new Error('Unexpected reply from the bug-report service.');
  }
  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('Unexpected reply from the bug-report service.');
  }
  const envelope = parsed as { success?: unknown; error?: unknown; data?: unknown };
  if (envelope.success !== true) {
    if (typeof envelope.error === 'string' && envelope.error) {
      throw new Error(envelope.error);
    }
    throw new Error(
      envelope.success === false ? 'The bug-report service refused the upload.' : 'Unexpected reply from the bug-report service.',
    );
  }
  const data = (envelope.data ?? {}) as Record<string, unknown>;
  const url = typeof data.url === 'string' ? data.url.trim() : '';
  const blobPath = typeof data.blob_path === 'string' ? data.blob_path : '';
  const filename = typeof data.filename === 'string' ? data.filename : '';
  if (!/^https?:\/\//i.test(url) || !blobPath || !filename) {
    throw new Error('Unexpected reply from the bug-report service.');
  }
  return { url, blobPath, filename };
}

/** The slice of the generated bug-report `DbConnection` the upload recipe needs. */
export interface BugReportConnectionLike {
  procedures: {
    requestAttachmentUploadUrl(args: { bugReportId: bigint; kind: { tag: ReportAttachmentKind } }): Promise<string>;
    requestImageUploadUrl(args: { bugReportId: bigint; imageIndex: number; ext: string }): Promise<string>;
  };
  reducers: {
    registerAttachment(args: {
      bugReportId: bigint;
      kind: { tag: ReportAttachmentKind };
      blobPath: string;
      size: number;
    }): unknown;
    registerImageAttachment(args: { bugReportId: bigint; filename: string; blobPath: string; size: number }): unknown;
  };
}

export type FetchLike = (url: string, init: RequestInit) => Promise<{ ok: boolean; status: number }>;

export interface UploadOptions {
  /** Injected for tests; wraps global fetch by default. */
  fetchImpl?: FetchLike;
  /** Presign call timeout; the SDK does not time out on its own. Default 30 s. */
  timeoutMs?: number;
}

const PRESIGN_TIMEOUT_MS = 30_000;

async function presign(call: Promise<string>, timeoutMs: number): Promise<UploadUrlTarget> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`Presign timed out after ${timeoutMs}ms`)), timeoutMs);
  });
  try {
    return parseUploadUrlEnvelope(await Promise.race([call, timeout]));
  } finally {
    clearTimeout(timer);
  }
}

async function putBlob(fetchImpl: FetchLike, target: UploadUrlTarget, body: Blob, contentType: string): Promise<void> {
  const res = await fetchImpl(target.url, {
    method: 'PUT',
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': contentType },
    body,
  });
  if (!res.ok) throw new Error(`blob upload failed (HTTP ${res.status})`);
}

/**
 * Attach a screenshot or log blob to an already-submitted report. Must run on
 * the connection/identity that submitted it. Throws on failure; callers treat
 * that as a warning, since the report itself is already filed.
 */
export async function uploadReportAttachment(
  conn: BugReportConnectionLike,
  reportId: bigint,
  kind: ReportAttachmentKind,
  blob: Blob,
  contentType: string,
  options: UploadOptions = {},
): Promise<void> {
  const fetchImpl = options.fetchImpl ?? ((url, init) => fetch(url, init));
  const target = await presign(
    conn.procedures.requestAttachmentUploadUrl({ bugReportId: reportId, kind: { tag: kind } }),
    options.timeoutMs ?? PRESIGN_TIMEOUT_MS,
  );
  await putBlob(fetchImpl, target, blob, contentType);
  await conn.reducers.registerAttachment({
    bugReportId: reportId,
    kind: { tag: kind },
    blobPath: target.blobPath,
    size: blob.size,
  });
}

/**
 * Attach one user-picked image into slot `imageIndex` (1-BASED). The server
 * derives the canonical key from the extension and rejects anything but its
 * own `blob_path` echoed back verbatim.
 */
export async function uploadReportImage(
  conn: BugReportConnectionLike,
  reportId: bigint,
  imageIndex: number,
  file: File,
  options: UploadOptions = {},
): Promise<void> {
  const ext = imageExtension(file.name);
  if (!ext || !(ext in IMAGE_CONTENT_TYPES)) {
    throw new Error('not a supported image type');
  }
  const fetchImpl = options.fetchImpl ?? ((url, init) => fetch(url, init));
  const target = await presign(
    conn.procedures.requestImageUploadUrl({ bugReportId: reportId, imageIndex, ext }),
    options.timeoutMs ?? PRESIGN_TIMEOUT_MS,
  );
  await putBlob(fetchImpl, target, file, imageContentType(ext, file.type));
  await conn.reducers.registerImageAttachment({
    bugReportId: reportId,
    filename: target.filename,
    blobPath: target.blobPath,
    size: file.size,
  });
}
